import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Button } from '@aimsos/ui';

import DataListFilterBar from '../components/DataListFilterBar.jsx';
import DataListFilterSlideout from '../components/DataListFilterSlideout.jsx';
import EntityListSkeleton from '../components/EntityListSkeleton.jsx';
import { AccessDeniedState, EmptyState, ErrorState } from '../components/states/index.js';
import TaskGroup from '../components/tasks/TaskGroup.jsx';
import {
  TASK_ACTIONS,
  TASK_GROUPS,
  TASK_SANDBOX_PATH,
  TASK_SANDBOX_SEARCH,
  TASK_SANDBOX_TAB_KIND,
  TASK_SEARCH_FIELDS,
  TASK_TYPES,
} from '../constants/tasks.js';
import { useDataListFilters } from '../hooks/useDataListFilters.js';
import { useTaskAction } from '../hooks/useTaskAction.js';
import { useTasks } from '../hooks/useTasks.js';

import { CheckCircle2, SearchX } from 'lucide-react';

const FILTER_DEFAULTS = Object.freeze({ searchFields: TASK_SEARCH_FIELDS });
const NO_SORTS = Object.freeze([]);

/**
 * The Tasks tab of a profile (CT-5722): the actions and human-in-the-loop tasks
 * waiting on this record, grouped Act now / This week / Later.
 *
 * A tab KIND (`TAB_KINDS.TASKS`); the tabs endpoint decides who gets it. Search
 * and the Type filter are the domain's toolbar, narrowed on the server; there is
 * no sort — the groups are the order.
 *
 * A row's action is dispatched by its TYPE through `TASK_ACTIONS`:
 *
 *   Review draft, Answer, Reply   the Concierge (CT-5721), handed off by the row itself
 *                                 (`TaskConciergeCard`) so the chat never re-renders this tab;
 *                                 disabled until the shell can open it
 *   Review workflow               the Next Best Action slide-out (CT-5713)
 *   Take over, Complete           mutations, then a quiet refetch
 *   Open in HubSpot               a new tab, `noopener,noreferrer`
 *   Review in Sandbox             Knowledge › Sandbox, filtered to what is left to
 *                                 confirm — hidden while the record has no Knowledge tab
 */
function TasksTabPage() {
  const { t } = useTranslation('unified-profiles');
  const { entityType, entityId, tabs = [], openTab, openNbaPanel, conciergeEntity, nbaWorkflowsStarted = 0 } =
    useOutletContext();
  // The record as the page names it (an untitled one included) — the same entity
  // the Next Best Action card hands the Concierge.
  const name = conciergeEntity.title;

  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);
  const filtersState = useDataListFilters(FILTER_DEFAULTS);
  const { filters, search, hasActiveQuery, clearAll } = filtersState;

  const { groups, loading, error, accessDenied, refetch, refresh } = useTasks({ entityType, entityId, filters, search });
  const { run, isRunning } = useTaskAction({ entityType, entityId, onDone: refresh });

  // A workflow started from the Next Best Action (its card or its slide-out)
  // takes the suggestion out of the list, and with it the rows linked to it:
  // re-read quietly, so "Review workflow" never points at a suggestion that is
  // gone (Bugbot, #568).
  const seenStartsRef = useRef(nbaWorkflowsStarted);
  useEffect(() => {
    if (nbaWorkflowsStarted === seenStartsRef.current) return;
    seenStartsRef.current = nbaWorkflowsStarted;
    refresh();
  }, [nbaWorkflowsStarted, refresh]);

  const sandboxTab = useMemo(() => tabs.find((tab) => tab.kind === TASK_SANDBOX_TAB_KIND), [tabs]);

  const filterDefs = useMemo(
    () => [
      {
        id: 'type',
        label: t('tasks.filters.type'),
        options: TASK_TYPES.map((value) => ({ value, label: t(`tasks.types.${value}`) })),
      },
    ],
    [t],
  );

  const actionStateOf = useCallback(
    (task) => {
      const action = TASK_ACTIONS[task.action?.type];
      if (!action) return 'hidden';
      if (action.kind === 'sandbox' && !(sandboxTab && openTab)) return 'hidden';
      if (action.kind === 'nbaPanel' && !openNbaPanel) return 'hidden';
      if (action.kind === 'external' && !task.action.ref) return 'hidden';
      return 'enabled';
    },
    [sandboxTab, openTab, openNbaPanel],
  );

  const handleAction = useCallback(
    (task) => {
      const action = TASK_ACTIONS[task.action?.type];
      if (!action) return;
      switch (action.kind) {
        case 'nbaPanel':
          openNbaPanel?.(task.action.ref, action.mode);
          break;
        case 'mutation':
          run(task, action.mutation);
          break;
        case 'external':
          window.open(task.action.ref, '_blank', 'noopener,noreferrer');
          break;
        case 'sandbox':
          if (sandboxTab) openTab?.(`${sandboxTab.id}/${TASK_SANDBOX_PATH}`, TASK_SANDBOX_SEARCH);
          break;
        default:
          break;
      }
    },
    [openNbaPanel, run, openTab, sandboxTab],
  );

  const handleMenu = useCallback((task, mutation) => run(task, mutation), [run]);

  const openAllFilters = useCallback(() => {
    filtersState.openDraft();
    setIsAllFiltersOpen(true);
  }, [filtersState]);
  const closeAllFilters = useCallback(() => setIsAllFiltersOpen(false), []);

  const byKey = useMemo(() => Object.fromEntries(groups.map((group) => [group.key, group.items])), [groups]);
  const total = groups.reduce((sum, group) => sum + group.items.length, 0);

  const results = () => {
    if (loading) return <EntityListSkeleton perPage={6} />;
    if (accessDenied) return <AccessDeniedState />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (total === 0) {
      return hasActiveQuery ? (
        <EmptyState
          testId="no-results-state"
          icon={SearchX}
          title={t('tasks.noResults.title')}
          subtitle={t('tasks.noResults.subtitle')}
          action={
            <Button variant="secondary" size="sm" onClick={clearAll}>
              {t('tasks.noResults.clear')}
            </Button>
          }
        />
      ) : (
        <EmptyState icon={CheckCircle2} title={t('tasks.empty.title', { name })} subtitle={t('tasks.empty.subtitle')} />
      );
    }

    return (
      <div className="flex flex-col gap-6">
        {TASK_GROUPS.map((key) => (
          <TaskGroup
            key={key}
            groupKey={key}
            items={byKey[key] ?? []}
            entity={conciergeEntity}
            actionStateOf={actionStateOf}
            isRunning={isRunning}
            onAction={handleAction}
            onMenu={handleMenu}
          />
        ))}
      </div>
    );
  };

  return (
    <div data-testid="tasks-tab" className="flex min-w-0 flex-1 flex-col gap-4 pb-6">
      <DataListFilterBar
        filtersState={filtersState}
        filterDefs={filterDefs}
        sortDefs={NO_SORTS}
        searchPlaceholder={t('tasks.searchPlaceholder')}
        onAllFiltersClick={openAllFilters}
      />
      <div className="flex min-w-0 flex-1 flex-col">{results()}</div>
      <DataListFilterSlideout
        isOpen={isAllFiltersOpen}
        onClose={closeAllFilters}
        filtersState={filtersState}
        filterDefs={filterDefs}
        sortDefs={NO_SORTS}
        showSort={false}
      />
    </div>
  );
}

export default TasksTabPage;
